/**
 * The Color — Cart JavaScript
 * Full AJAX cart: add to cart, update quantity, remove item, cart drawer open/close,
 * cart count badge update, free shipping threshold calculation.
 * Uses Shopify /cart.js, /cart/add.js, /cart/change.js endpoints.
 */

(function () {
  'use strict';

  /**
   * Build a Shopify CDN URL with a specific width parameter.
   * Handles both legacy _600x suffix URLs and modern ?width= URLs.
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    var url = src.replace(/_\d+x(\d+)?(?=\.|$)/g, '');
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      /* Return full URL if CDN origin differs from current page */
      if (parsed.origin !== window.location.origin) {
        return parsed.href;
      }
      return parsed.pathname + parsed.search;
    } catch (e) {
      url = url.replace(/[?&]width=\d+/g, '');
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + width;
    }
  }

  /* --------------------------------------------------------------------------
     1. Configuration & Selectors
     -------------------------------------------------------------------------- */

  const config = {
    freeShippingThreshold: 5000, // £50.00 in pence — updated dynamically from theme settings
  };

  const selectors = {
    cartDrawer: '[data-cart-drawer]',
    cartDrawerClose: '[data-action="close-cart"]',
    cartDrawerOpen: '[data-action="open-cart"]',
    cartCount: '[data-cart-count], [data-cart-drawer-count]',
    cartItems: '[data-cart-items]',
    cartSubtotal: '[data-cart-subtotal]',
    cartEmpty: '[data-cart-empty]',
    cartFooter: '[data-cart-footer]',
    shippingBar: '[data-cart-shipping-bar]',
    shippingProgress: '[data-shipping-fill]',
    shippingMessage: '[data-shipping-text]',
    addToCart: '[data-add-to-cart]',
    lineItemRemove: '[data-action="remove-item"]',
    lineItemQuantity: '[data-quantity-input]',
    lineItemMinus: '[data-action="decrease-quantity"]',
    lineItemPlus: '[data-action="increase-quantity"]',
  };

  /* --------------------------------------------------------------------------
     2. Cart State
     -------------------------------------------------------------------------- */

  let isUpdating = false;

  /* --------------------------------------------------------------------------
     3. Shopify Cart API
     -------------------------------------------------------------------------- */

  const CartAPI = {
    /**
     * Fetch current cart state
     */
    async get() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    },

    /**
     * Add item to cart
     */
    async add(variantId, quantity = 1, properties = {}) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
              properties: properties,
            },
          ],
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }
      return response.json();
    },

    /**
     * Update item quantity by line index (1-based)
     */
    async change(line, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return response.json();
    },

  };

  /* --------------------------------------------------------------------------
     4. Cart Drawer UI
     -------------------------------------------------------------------------- */

  const CartDrawer = {
    drawer: null,
    releaseFocus: null,
    previousFocus: null,

    init() {
      this.drawer = document.querySelector(selectors.cartDrawer);
      if (!this.drawer) return;

      // Open buttons
      document.querySelectorAll(selectors.cartDrawerOpen).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close button
      const closeBtn = this.drawer.querySelector(selectors.cartDrawerClose);
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      // Overlay click (dispatched from theme.js)
      document.addEventListener('overlay:clicked', () => {
        if (this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      // Read free shipping threshold from global theme settings (set in theme.liquid)
      if (window.theme && window.theme.freeShippingThreshold) {
        config.freeShippingThreshold = window.theme.freeShippingThreshold;
      }
    },

    open() {
      if (!this.drawer) return;
      this.previousFocus = document.activeElement;
      this.drawer.classList.add('is-open');

      // Fire view_cart event for GA4
      CartAPI.get().then(function (cart) { GtagEvents.viewCart(cart); }).catch(function () {});

      // Let Overlay handle scroll-lock (saves scroll position BEFORE
      // adding position:fixed, then restores it on close).
      if (window.TheColor && window.TheColor.Overlay) {
        window.TheColor.Overlay.show();
      }

      // Trap focus
      if (window.TheColor && window.TheColor.trapFocus) {
        this.releaseFocus = window.TheColor.trapFocus(this.drawer);
      }
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');

      // Let Overlay handle scroll-unlock (removes drawer-open class,
      // clears body.style.top, and restores scroll position).
      if (window.TheColor && window.TheColor.Overlay) {
        window.TheColor.Overlay.hide();
      }

      if (this.releaseFocus) this.releaseFocus();
      if (this.previousFocus) this.previousFocus.focus();
    },
  };

  /* --------------------------------------------------------------------------
     5. Cart Rendering
     -------------------------------------------------------------------------- */

  const CartRenderer = {
    formatMoney(pence) {
      return window.TheColor.utils.formatMoney(pence);
    },

    /**
     * Update cart count badges
     */
    updateCount(count) {
      document.querySelectorAll(selectors.cartCount).forEach((el) => {
        /* Read previous count so we can detect "item added" and trigger the
           bounce animation. Using textContent on the visible numeric target
           keeps this resilient to the el.textContent / aria-hidden split. */
        const numericTarget = el.querySelector('[aria-hidden="true"]');
        const readEl = numericTarget || el;
        const previousText = (readEl.textContent || '').replace(/[^\d]/g, '');
        const previousCount = parseInt(previousText, 10) || 0;

        if (numericTarget) {
          numericTarget.textContent = el.hasAttribute('data-cart-drawer-count') ? `(${count})` : count;
        } else {
          el.textContent = count;
        }
        el.style.display = count > 0 ? '' : 'none';

        /* Trigger bounce when count grows (covers first-add and qty increase).
           Skip for the drawer-internal counter so the bounce only fires on
           the header badge. Respects prefers-reduced-motion via CSS. */
        if (count > previousCount && !el.hasAttribute('data-cart-drawer-count')) {
          el.classList.remove('is-bumped');
          /* Force reflow so animation restarts on rapid successive adds */
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      });
    },

    /**
     * Update shipping progress bar
     */
    updateShippingBar(totalPrice) {
      const bar = document.querySelector(selectors.shippingBar);
      if (!bar) return;

      const progress = document.querySelector(selectors.shippingProgress);
      const message = document.querySelector(selectors.shippingMessage);
      if (!progress || !message) return;

      if (totalPrice >= config.freeShippingThreshold) {
        progress.style.width = '100%';
        message.textContent = '';
        const strong = document.createElement('strong');
        strong.textContent = 'You\'ve unlocked free delivery!';
        message.appendChild(strong);
        bar.classList.add('tc-shipping-bar--complete');
      } else {
        const remaining = config.freeShippingThreshold - totalPrice;
        const percent = Math.min((totalPrice / config.freeShippingThreshold) * 100, 100);
        progress.style.width = percent + '%';
        message.textContent = 'Spend ';
        const strong = document.createElement('strong');
        strong.textContent = this.formatMoney(remaining);
        message.appendChild(strong);
        message.appendChild(document.createTextNode(' more for free delivery'));
        bar.classList.remove('tc-shipping-bar--complete');
      }
    },

    /**
     * Update subtotal
     */
    updateSubtotal(totalPrice) {
      document.querySelectorAll(selectors.cartSubtotal).forEach((el) => {
        el.textContent = this.formatMoney(totalPrice);
      });
    },

    /**
     * Show/hide empty state, items list and footer.
     * Both [data-cart-items] and [data-cart-empty] are always in the DOM
     * (see snippets/cart-drawer.liquid) so these selectors always resolve.
     */
    updateEmptyState(itemCount) {
      const empty = document.querySelector(selectors.cartEmpty);   // [data-cart-empty]
      const items = document.querySelector(selectors.cartItems);   // [data-cart-items]
      const footer = document.querySelector(selectors.cartFooter); // [data-cart-footer]

      if (empty)  empty.style.display  = itemCount === 0 ? '' : 'none';
      if (items)  items.style.display  = itemCount > 0  ? '' : 'none';
      if (footer) footer.style.display = itemCount > 0  ? '' : 'none';
    },

    /**
     * Render all cart line items
     */
    renderItems(cart) {
      const container = document.querySelector(selectors.cartItems);
      if (!container) return;

      if (cart.items.length === 0) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = cart.items
        .map(
          (item, index) => `
        <li class="tc-cart-drawer__item" data-line-index="${index + 1}" data-variant-id="${item.variant_id}">
          <div class="tc-cart-drawer__item-image">
            <a href="${this.escapeAttr(item.url)}" aria-label="${this.escapeHtml(item.product_title)}">
              ${
                (item.featured_image && item.featured_image.url)
                  ? `<img src="${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 160))}" srcset="${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 160))} 1x, ${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 240))} 2x" alt="${this.escapeHtml((item.featured_image.alt || item.product_title))}" width="80" height="80" loading="lazy">`
                  : (item.image
                    ? `<img src="${this.escapeAttr(getSizedImageUrl(item.image, 160))}" alt="${this.escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">`
                    : '<div class="tc-cart-drawer__item-placeholder"></div>')
              }
            </a>
          </div>
          <div class="tc-cart-drawer__item-details">
            <a href="${this.escapeAttr(item.url)}" class="tc-cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
            ${item.variant_title ? `<p class="tc-cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
            <div class="tc-cart-drawer__item-controls">
              <div class="tc-cart-drawer__quantity">
                <button type="button" class="tc-cart-drawer__quantity-btn" data-action="decrease-quantity" data-line="${index + 1}" aria-label="Decrease quantity of ${this.escapeHtml(item.product_title)}">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
                <input
                  type="number"
                  class="tc-cart-drawer__quantity-input"
                  data-quantity-input
                  data-line="${index + 1}"
                  value="${item.quantity}"
                  min="0"
                  max="${item.quantity + 10}"
                  aria-label="Quantity of ${this.escapeHtml(item.product_title)}"
                >
                <button type="button" class="tc-cart-drawer__quantity-btn" data-action="increase-quantity" data-line="${index + 1}" aria-label="Increase quantity of ${this.escapeHtml(item.product_title)}">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
              </div>
              <p class="tc-cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
            </div>
          </div>
          <button type="button" class="tc-cart-drawer__remove" data-action="remove-item" data-line="${index + 1}" aria-label="Remove ${this.escapeHtml(item.product_title)} from cart">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </li>
      `
        )
        .join('');

      // Bind new event listeners
      this.bindItemListeners();
    },

    escapeHtml(text) {
      return window.TheColor.utils.escapeHtml(text);
    },

    escapeAttr(str) {
      return window.TheColor.utils.escapeAttr(str);
    },

    /**
     * Full cart update render
     */
    render(cart) {
      this.updateCount(cart.item_count);
      this.updateSubtotal(cart.total_price);
      this.updateShippingBar(cart.total_price);
      this.updateEmptyState(cart.item_count);
      this.renderItems(cart);
    },

    /**
     * Bind event listeners to dynamically rendered line items
     */
    bindItemListeners() {
      const container = document.querySelector(selectors.cartItems);
      if (!container || container._tcDelegated) return;
      container._tcDelegated = true;

      let debounceTimer;

      // Single delegated click handler for all cart item buttons
      container.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        e.preventDefault();
        const line = parseInt(btn.dataset.line, 10);
        const action = btn.dataset.action;

        if (action === 'remove-item') {
          CartActions.updateQuantity(line, 0);
        } else if (action === 'decrease-quantity') {
          const input = container.querySelector(
            `${selectors.lineItemQuantity}[data-line="${line}"]`
          );
          if (input) CartActions.updateQuantity(line, Math.max(0, parseInt(input.value, 10) - 1));
        } else if (action === 'increase-quantity') {
          const input = container.querySelector(
            `${selectors.lineItemQuantity}[data-line="${line}"]`
          );
          if (input) CartActions.updateQuantity(line, parseInt(input.value, 10) + 1);
        }
      });

      // Single delegated change handler for quantity inputs
      container.addEventListener('change', (e) => {
        const input = e.target.closest(selectors.lineItemQuantity);
        if (!input) return;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const line = parseInt(input.dataset.line, 10);
          const newQty = Math.max(0, parseInt(input.value, 10) || 0);
          CartActions.updateQuantity(line, newQty);
        }, 300);
      });
    },
  };

  /* --------------------------------------------------------------------------
     6. Google Ads E-Commerce Events
     -------------------------------------------------------------------------- */

  const GtagEvents = {
    /**
     * Map cart items to GA4 items array format
     */
    mapItems(cartItems) {
      return cartItems.map(function (item, index) {
        return {
          item_id: '' + item.product_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_variant: item.variant_title || undefined,
          price: item.final_price / 100,
          quantity: item.quantity,
          index: index,
        };
      });
    },

    /**
     * Fire begin_checkout event
     */
    beginCheckout(cart) {
      if (typeof gtag !== 'function' || !cart || !cart.items.length || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'begin_checkout', {
        currency: 'GBP',
        value: cart.total_price / 100,
        items: this.mapItems(cart.items),
      });
    },

    /**
     * Fire remove_from_cart event for items being removed or decreased
     */
    removeFromCart(item, removedQty) {
      if (typeof gtag !== 'function' || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'remove_from_cart', {
        currency: 'GBP',
        value: (item.final_price / 100) * removedQty,
        items: [{
          item_id: '' + item.product_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_variant: item.variant_title || undefined,
          price: item.final_price / 100,
          quantity: removedQty,
        }],
      });
    },

    /**
     * Fire view_cart event
     */
    viewCart(cart) {
      if (typeof gtag !== 'function' || !cart || !cart.items.length || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'view_cart', {
        currency: 'GBP',
        value: cart.total_price / 100,
        items: this.mapItems(cart.items),
      });
    },
  };

  /* --------------------------------------------------------------------------
     7. Cart Actions
     -------------------------------------------------------------------------- */

  const CartActions = {
    /**
     * Add item to cart and open drawer
     */
    async addToCart(variantId, quantity = 1, properties = {}) {
      if (isUpdating) return;
      isUpdating = true;

      try {
        await CartAPI.add(variantId, quantity, properties);
        const cart = await CartAPI.get();
        CartRenderer.render(cart);
        CartDrawer.open();
      } catch (error) {
        // Silent in production — surfaced to user via cart:error toast
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            detail: { message: error.message },
          })
        );
      } finally {
        isUpdating = false;
      }
    },

    /**
     * Fire begin_checkout and navigate to checkout
     */
    async beginCheckout() {
      try {
        const cart = await CartAPI.get();
        GtagEvents.beginCheckout(cart);
      } catch (e) {
        // Don't block checkout on tracking failure
      }
    },

    /**
     * Update line item quantity (0 to remove)
     */
    async updateQuantity(line, quantity) {
      if (isUpdating) return;
      isUpdating = true;

      // Capture current item data before update for remove_from_cart tracking
      let previousCart = null;
      try {
        previousCart = await CartAPI.get();
      } catch (e) { /* proceed without tracking */ }

      // Optimistic UI: add loading state
      const drawer = document.querySelector(selectors.cartDrawer);
      if (drawer) drawer.classList.add('is-loading');

      try {
        const cart = await CartAPI.change(line, quantity);

        // Fire remove_from_cart if quantity decreased
        if (previousCart && previousCart.items[line - 1]) {
          const prevItem = previousCart.items[line - 1];
          const newItem = cart.items.find(function (i) { return i.variant_id === prevItem.variant_id; });
          const newQty = newItem ? newItem.quantity : 0;
          if (newQty < prevItem.quantity) {
            GtagEvents.removeFromCart(prevItem, prevItem.quantity - newQty);
          }
        }

        CartRenderer.render(cart);
      } catch (error) {
        // Silent in production — surfaced to user via cart:error toast
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            detail: { message: error.message },
          })
        );
        // Refresh cart to restore correct state
        const cart = await CartAPI.get();
        CartRenderer.render(cart);
      } finally {
        isUpdating = false;
        if (drawer) drawer.classList.remove('is-loading');
      }
    },

    /**
     * Initialise cart state on page load
     */
    async init() {
      try {
        const cart = await CartAPI.get();
        CartRenderer.render(cart);
      } catch (error) {
        // Silent — cart init failure shouldn't blow up the page
      }
    },
  };

  /* --------------------------------------------------------------------------
     7. Add-to-Cart Form Binding
     -------------------------------------------------------------------------- */

  function bindAddToCartForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;
      // Product page form is handled by product.js — skip to avoid duplicate add
      if (form.hasAttribute('data-product-form')) return;

      e.preventDefault();

      const variantInput = form.querySelector('[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value, 10);
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // Collect properties
      const properties = {};
      form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const key = input.name.match(/properties\[(.+)\]/)?.[1];
        if (key && input.value) {
          properties[key] = input.value;
        }
      });

      // Disable button during add
      const submitBtn = form.querySelector(selectors.addToCart) || form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      CartActions.addToCart(variantId, quantity, properties).then(() => {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      });
    });

    // Quick-add buttons (not in forms)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();
      const variantId = parseInt(btn.dataset.quickAdd, 10);
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('is-loading');

      CartActions.addToCart(variantId).then(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
    });
  }

  /* --------------------------------------------------------------------------
     8. Cart Error Toast
     -------------------------------------------------------------------------- */

  const CartErrorToast = {
    el: null,
    hideTimer: null,

    init() {
      // Create toast element
      this.el = document.createElement('div');
      this.el.className = 'tc-cart-toast';
      this.el.setAttribute('role', 'alert');
      this.el.setAttribute('aria-live', 'assertive');
      this.el.style.display = 'none';
      document.body.appendChild(this.el);

      // Listen for cart errors
      document.addEventListener('cart:error', (e) => {
        const msg = (e.detail && e.detail.message) || 'Something went wrong. Please try again.';
        this.show(msg);
      });
    },

    show(message) {
      if (!this.el) return;
      clearTimeout(this.hideTimer);
      this.el.textContent = message;
      this.el.style.display = '';
      requestAnimationFrame(() => {
        this.el.classList.add('is-visible');
      });
      this.hideTimer = setTimeout(() => this.hide(), 4000);
    },

    hide() {
      if (!this.el) return;
      this.el.classList.remove('is-visible');
      setTimeout(() => {
        this.el.style.display = 'none';
      }, 300);
    },
  };

  /* --------------------------------------------------------------------------
     9. Initialise
     -------------------------------------------------------------------------- */

  function init() {
    CartDrawer.init();
    CartActions.init();
    CartErrorToast.init();
    bindAddToCartForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.TheColor = window.TheColor || {};
  window.TheColor.Cart = CartActions;
  window.TheColor.CartDrawer = CartDrawer;
  window.TheColor.GtagEvents = GtagEvents;
})();
